/**
 * What each Study Documents download asks the server for (Jim's plan,
 * 2026-10-25). The card face and the document's View panel read the same
 * options from here, so a document downloads the same way from both places.
 *
 * Pure: no Firebase import. The CRF Instruction Guide and the Informed Consent
 * Form need Firestore reads or a client-side Word build, so their requests are
 * assembled where those live (DataManagementHub.tsx, consentDownload.ts).
 */
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { ddGateRefusalMessage, gateRefusalMessage, type DdDownloadResponseLike } from '../verification/ddGateMessages';
import { acrfErrorMessage } from '../dataManagement/acrfErrorMessage';

export interface CallableSpec {
  name: string;
  args: Record<string, unknown>;
  /** Client wait in ms; omitted means the SDK default. */
  timeout?: number;
}

export interface DownloadRequest {
  /** Unique per document and format, e.g. "dmp:word". */
  id: string;
  /** Plain document name used in every message. */
  docName: string;
  /** Show "This can take a few minutes" and the elapsed time while it runs. */
  slow?: boolean;
  /** Runs first, before the pre-check (the SDTM panel clears its notice). */
  onStart?: () => void;
  /** Server pre-check; returns the refusal to show, or null to go ahead. */
  precheck?: () => Promise<string | null>;
  /** A callable to run first (the annotated CRF is built, then downloaded). */
  prepare?: CallableSpec;
  /** The download callable. */
  callable?: CallableSpec;
  /** Client-built document (consent); 'saved' when the builder saved it itself. */
  build?: () => Promise<{ blob: Blob; filename: string } | 'saved'>;
  /** The filename when the server names none. */
  fallbackFilename?: string;
  /** A client-built filename that wins over the server's. */
  filename?: (data: unknown) => Promise<string> | string;
  /** Refusal line for a soft {blocked} reply. */
  refusal?: (data: DdDownloadResponseLike) => string | null;
  /** A reply with no file in it (legacy guide markdown). Must save or throw. */
  onUnrecognised?: (data: unknown) => Promise<'saved'>;
  /** Shown when the server says there is nothing to download. */
  nothingMessage?: string;
  onNothing?: () => void;
  /** A document-specific error line; null falls back to the standard one. */
  describeError?: (err: unknown) => string | null;
}

export interface DownloadOption {
  /** Menu label: the format, e.g. "Word", "CSV". */
  label: string;
  title?: string;
  request: DownloadRequest;
}

/** Plain names, one per downloadable document on the page. */
export const DOWNLOAD_DOC_NAMES = {
  dataDictionary: 'Data Dictionary',
  crfInstructionGuide: 'CRF Instruction Guide',
  dataManagementPlan: 'Data Management Plan',
  editChecks: 'Edit Check Specification',
  sdtmMapping: 'SDTM Mapping Specification',
  defineXml: 'Define-XML',
  sdrg: "SDTM Reviewer's Guide",
  suppqual: 'SUPPQUAL Templates',
  acrf: 'Annotated CRF',
  submissionPackage: 'SDTM Submission Package',
  edts: 'External Data Transfer Specification',
  medicalCodingSpec: 'Medical Coding Specification',
  drcp: 'Data Review and Cleaning Plan',
  saerp: 'SAE Reconciliation Procedure',
  dblc: 'Database Lock Checklist',
  visitFormMatrix: 'Visit & Form Matrix',
  consentForm: 'Informed Consent Form',
} as const;

export type DownloadCardId =
  | 'dataDictionary'
  | 'dataManagementPlan'
  | 'editChecks'
  | 'sdtmMapping'
  | 'edts'
  | 'medicalCodingSpec'
  | 'drcp'
  | 'saerp'
  | 'dblc'
  | 'visitFormMatrix';

export interface DownloadContext {
  projectId: string;
  /** studySetup.edcSystem; REDCap studies also get the REDCap CSV. */
  edcSystem?: string;
  /** Called when the SUPPQUAL download finds no SUPP-- domains. */
  onSuppqualNothing?: () => void;
  /** Called when any SDTM download starts (the panel clears its notice). */
  onDownloadStart?: () => void;
}

export const SUPPQUAL_NOTHING_MESSAGE = 'No SUPPQUAL templates — no SUPP-- domains in the current mapping.';

/** A single Word download for the prose documents that all reply with base64. */
function word(id: string, docName: string, callable: string, timeout: number | undefined, projectId: string): DownloadOption {
  return {
    label: 'Word',
    title: `Download the ${docName} as a Word document`,
    request: {
      id: `${id}:word`,
      docName,
      callable: { name: callable, args: { projectId }, ...(timeout ? { timeout } : {}) },
      fallbackFilename: `${docName.replace(/[^A-Za-z0-9]+/g, '_')}.docx`,
    },
  };
}

/**
 * The download options for a card, in menu order. One option means the
 * Download button downloads it in one click; more than one opens a menu.
 */
export function studyDocumentDownloadOptions(card: DownloadCardId, ctx: DownloadContext): DownloadOption[] {
  const { projectId } = ctx;
  const N = DOWNLOAD_DOC_NAMES;
  switch (card) {
    case 'dataDictionary': {
      // The gate may run a verification pass inline before serving (inc5
      // step E): allow for it, and say it can take a while.
      const dd = (format: 'cdash' | 'redcap'): DownloadOption => ({
        label: format === 'redcap' ? 'REDCap CSV' : 'CDASH CSV',
        title: format === 'redcap'
          ? 'Download the REDCap import CSV (numbered choices, one per real answer choice)'
          : 'Download the data dictionary (CDASH format) as a CSV',
        request: {
          id: `dataDictionary:${format}`,
          docName: N.dataDictionary,
          slow: true,
          callable: {
            name: CLOUD_FUNCTIONS.DOWNLOAD_DATA_DICTIONARY,
            args: format === 'redcap' ? { projectId, edcFormat: 'redcap' } : { projectId },
            timeout: CALLABLE_TIMEOUTS.MEDIUM,
          },
          fallbackFilename: format === 'redcap' ? 'DataDictionary_REDCap.csv' : 'DataDictionary.csv',
          refusal: ddGateRefusalMessage,
        },
      });
      return ctx.edcSystem === 'redcap' ? [dd('cdash'), dd('redcap')] : [dd('cdash')];
    }
    case 'dataManagementPlan':
      // exportDMPDocxV2 may run 120 s; the SDK default (70 s) was shorter.
      return [word('dataManagementPlan', N.dataManagementPlan, CLOUD_FUNCTIONS.EXPORT_DMP_DOCX, CALLABLE_TIMEOUTS.SHORT, projectId)];
    case 'editChecks': {
      const refusal = (d: DdDownloadResponseLike) => gateRefusalMessage('edit check specification', d);
      return [
        {
          label: 'Word',
          title: 'Download the edit check specification as a Word document',
          request: {
            id: 'editChecks:word',
            docName: N.editChecks,
            callable: { name: CLOUD_FUNCTIONS.EXPORT_EDIT_CHECK_SPEC_DOCX, args: { projectId }, timeout: CALLABLE_TIMEOUTS.MEDIUM },
            fallbackFilename: 'Edit_Check_Specification.docx',
            refusal,
          },
        },
        {
          label: 'CSV',
          title: 'Download the edit check specification as a CSV',
          request: {
            id: 'editChecks:csv',
            docName: N.editChecks,
            callable: { name: CLOUD_FUNCTIONS.EXPORT_EDIT_CHECK_SPEC_CSV, args: { projectId }, timeout: CALLABLE_TIMEOUTS.MEDIUM },
            fallbackFilename: 'Edit_Check_Specification.csv',
            refusal,
          },
        },
      ];
    }
    case 'sdtmMapping': {
      // GENERATOR_XLARGE (31 min) accommodates the SDTM exporters that run at
      // the Gen-2 1800s server ceiling (Reviewer's Guide, Submission Package).
      const T = CALLABLE_TIMEOUTS.GENERATOR_XLARGE;
      const sdtm = (
        key: string,
        label: string,
        docName: string,
        callable: string,
        fallbackFilename: string,
        extra: Partial<DownloadRequest> = {},
      ): DownloadOption => ({
        label,
        request: {
          id: `sdtm:${key}`,
          docName,
          callable: { name: callable, args: { projectId }, timeout: T },
          fallbackFilename,
          ...(ctx.onDownloadStart ? { onStart: ctx.onDownloadStart } : {}),
          ...extra,
        },
      });
      return [
        sdtm('mappingSpec', 'Mapping Specification (Word)', N.sdtmMapping, 'exportSDTMMappingV2', 'sdtm-mapping-specification.docx'),
        sdtm('defineXml', 'Define-XML', N.defineXml, 'exportSDTMDefineXmlV2', 'define.xml'),
        sdtm('sdrg', "Reviewer's Guide (Word)", N.sdrg, 'exportSDTMReviewersGuideV2', 'sdrg.docx', { slow: true }),
        sdtm('suppqual', 'SUPPQUAL Templates (ZIP)', N.suppqual, 'exportSDTMSuppqualTemplatesV2', 'suppqual-templates.zip', {
          nothingMessage: SUPPQUAL_NOTHING_MESSAGE,
          ...(ctx.onSuppqualNothing ? { onNothing: ctx.onSuppqualNothing } : {}),
        }),
        // One click: build the annotated CRF (renders the PDF), then download it.
        // A refusal carries the server's own reason (approve a form, generate
        // the mapping first), shown as is.
        sdtm('acrf', 'Annotated CRF (PDF)', N.acrf, CLOUD_FUNCTIONS.DOWNLOAD_ANNOTATED_CRF, 'annotated-crf.pdf', {
          slow: true,
          prepare: { name: CLOUD_FUNCTIONS.GENERATE_ANNOTATED_CRF, args: { projectId }, timeout: T },
          describeError: (err) => acrfErrorMessage(err as { code?: string; message?: string }),
        }),
        sdtm('package', 'Submission Package (ZIP)', N.submissionPackage, 'exportSDTMSubmissionPackageV2', 'sdtm-submission-package.zip', { slow: true }),
      ];
    }
    case 'edts':
      return [word('edts', N.edts, CLOUD_FUNCTIONS.EXPORT_EDTS_DOCX, CALLABLE_TIMEOUTS.LONG, projectId)];
    case 'medicalCodingSpec':
      return [word('medicalCodingSpec', N.medicalCodingSpec, CLOUD_FUNCTIONS.EXPORT_MCS_DOCX, CALLABLE_TIMEOUTS.LONG, projectId)];
    case 'drcp':
      return [word('drcp', N.drcp, CLOUD_FUNCTIONS.EXPORT_DRCP_DOCX, CALLABLE_TIMEOUTS.LONG, projectId)];
    case 'saerp':
      return [word('saerp', N.saerp, CLOUD_FUNCTIONS.EXPORT_SAERP_DOCX, CALLABLE_TIMEOUTS.LONG, projectId)];
    case 'dblc':
      return [word('dblc', N.dblc, CLOUD_FUNCTIONS.EXPORT_DBLC_DOCX, CALLABLE_TIMEOUTS.LONG, projectId)];
    case 'visitFormMatrix': {
      const vfm = (format: 'docx' | 'csv' | 'odm', label: string): DownloadOption => ({
        label,
        title: `Download the visit/form matrix as ${label}`,
        request: {
          id: `visitFormMatrix:${format}`,
          docName: N.visitFormMatrix,
          callable: { name: CLOUD_FUNCTIONS.EXPORT_VISIT_FORM_MATRIX_V2, args: { projectId, format }, timeout: CALLABLE_TIMEOUTS.MEDIUM },
          fallbackFilename: `Visit_Form_Matrix.${format === 'odm' ? 'xml' : format}`,
          describeError: (err) =>
            (err as { code?: string } | null)?.code === 'functions/failed-precondition'
              ? 'Assign forms to visits and save the matrix before downloading it.'
              : null,
        },
      });
      return [vfm('docx', 'Word'), vfm('csv', 'CSV'), vfm('odm', 'ODM-XML')];
    }
  }
}
